'use strict';

Object.defineProperty(exports, "__esModule", { value: true });
exports.competitions = void 0;
var tslib_1 = require("tslib");
var adapter_1 = require("../adapter");
var Competitions = /** @class */ (function () {
    function Competitions() {
    }
    /**
     * Get paginated leaderboard-like set of results by achievement, ordered by achievement in descending order
     */
    Competitions.prototype.leaderboardByAchievement = function (achievement, page, itemsPerPage) {
        var params = { achievement: achievement, page: page, itemsPerPage: itemsPerPage };
        return adapter_1.adapter.competitions.leaderboardByAchievement(params);
    };
    /**
     * Get upcoming competitions
     */
    Competitions.prototype.upcoming = function (options) {
        return adapter_1.adapter.competitions.upcoming(options);
    };
    /**
     * Get all competitions
     *
     * @param [userName] If set returns only user competitions
     */
    Competitions.prototype.all = function (options) {
        var _this = this;
        return adapter_1.adapter.competitions
            .all(options)
            .then(function (allCompetitions) {
            return allCompetitions.map(_this.addCurrentMilestone.bind(_this));
        });
    };
    /**
     * Gets a competition by slug or by username & demographic filters.
     * @param params
     */
    Competitions.prototype.get = function (params) {
        return adapter_1.adapter.competitions.get(params);
    };
    /**
     * Opt's in to the specified competition. Direct opt in must be enabled
     * in the CMS for this to work.
     * @param competitionId The magic ID of the competition
     */
    Competitions.prototype.optIn = function (competitionId) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var res;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, adapter_1.adapter.competitions.optIn({ competitionId: competitionId })];
                    case 1:
                        res = _a.sent();
                        return [2 /*return*/, res.success];
                }
            });
        });
    };
    /**
     * Opt's out from the specified competition. Direct opt in must be enabled
     * in the CMS for this to work.
     * @param competitionId The magic ID of the competition
     */
    Competitions.prototype.optOut = function (competitionId) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var res;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, adapter_1.adapter.competitions.optOut({ competitionId: competitionId })];
                    case 1:
                        res = _a.sent();
                        return [2 /*return*/, res.success];
                }
            });
        });
    };
    /**
     * Get the list of scheduled competitions.
     * If the `onlyOptIn` parameter is passed as true, then only
     * the the competitions where the user opted in are returned
     */
    Competitions.prototype.getScheduled = function (params) {
        return adapter_1.adapter.competitions.getScheduled(params);
    };
    Competitions.prototype.getScheduledDetails = function () {
        return adapter_1.adapter.competitions.getScheduledDetails();
    };
    /**
     * Find the milestone closest to the user’s achievement count.
     * All milestones have a `value` and we need to find the one closest
     * to the passed `value`.
     * If milestones are something like
     * ```[{ value: 5 },{ value: 10 },{ value: 20 }]```
     * and the user has an achievement count of 9, we want to find
     * the second milestone.
     *
     * @param milestones
     * @param achievementValue
     * @param minAmount defaults to 0
     */
    Competitions.prototype.resolveMilestoneData = function (milestones, achievementValue, minAmount) {
        if (minAmount === void 0) { minAmount = 0; }
        if (!Array.isArray(milestones)) {
            throw TypeError();
        }
        if (!Number.isFinite(achievementValue)) {
            throw TypeError();
        }
        var previous = { value: 0, winAmount: 0 };
        var milestonesCopy = Array.prototype.slice.call(milestones);
        for (var index = 0; index < milestonesCopy.length; index++) {
            var end = achievementValue >= milestonesCopy[index].value &&
                !milestonesCopy[index].loopMultiplier &&
                index === milestonesCopy.length - 1;
            if (achievementValue < milestonesCopy[index].value || end) {
                return this.milestoneData(milestonesCopy[index], previous, achievementValue, minAmount, index, end);
            }
            // Support for infinite number of milestones
            var multiplier = Number(milestonesCopy[index].loopMultiplier);
            if (multiplier >= 1 && index === milestonesCopy.length - 1) {
                var spread = milestonesCopy[index].value - previous.value;
                var clonedMilestone = tslib_1.__assign({}, milestonesCopy[index]);
                clonedMilestone.value =
                    clonedMilestone.value + spread * multiplier;
                milestonesCopy.push(clonedMilestone);
            }
            previous = milestonesCopy[index];
        }
        // If this happens it means the user’s achievement value is greater than the last milestone.
        throw new Error('Milestone value out of range');
    };
    /**
     * Adds current milestone information to a competition
     */
    Competitions.prototype.addCurrentMilestone = function (competition) {
        var currentMilestone;
        try {
            currentMilestone = this.resolveMilestoneData(competition.milestones, competition.achievementValue, competition.minAmount);
        }
        catch (e) {
            console.error(e);
        }
        return tslib_1.__assign(tslib_1.__assign({}, competition), { currentMilestone: currentMilestone });
    };
    /**
     * Resolves the actual step for the milestone progress
     *
     * @param milestone Milestone object
     * @param previous Previous milestone object
     * @param achievementValue Achievement value
     * @param minAmount Min amount
     * @param index A value to calculate the level
     * @param end Boolean value tu know if the milestone is completed or not
     *
     * @returns A new milestone object with all the calculated fields
     */
    Competitions.prototype.milestoneData = function (milestone, previous, achievementValue, minAmount, index, end) {
        var maxValue = milestone.value - previous.value;
        var value = maxValue + achievementValue - milestone.value;
        var percentage = Math.floor((value / maxValue) * 100);
        var step = this.resolveSteps(maxValue);
        return {
            isComplete: end,
            level: index + 1,
            min: 0,
            max: maxValue,
            percentage: percentage,
            step: step,
            type: step === 0 ? 'percent' : 'numeric',
            remaining: maxValue - value,
            remainingPercentage: 100 - percentage,
            value: value,
            win: milestone.winAmount,
            minAmount: minAmount,
            isRoleChange: milestone.hasOwnProperty('roles'),
            isSegmentChange: milestone.hasOwnProperty('segment'),
            token: milestone.token,
        };
    };
    /**
     * Resolves the actual step for the milestone progress
     *
     * @returns A value to show
     */
    Competitions.prototype.resolveSteps = function (maxValue) {
        if (maxValue > 1000) {
            return 0;
        }
        else if (maxValue > 100) {
            return 100;
        }
        else if (maxValue > 10) {
            return 10;
        }
        else {
            return 1;
        }
    };
    return Competitions;
}());
exports.competitions = new Competitions();
